import { lazy, Suspense } from 'react';
import { NavLink, Navigate, Outlet, Route, Routes } from 'react-router';
import { PageHeader } from '@/components/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';

const lz = <K extends string>(load: () => Promise<Record<K, React.ComponentType>>, name: K) => lazy(() => load().then((m) => ({ default: m[name] })));
const Overview = lz(() => import('./pages/overview-page'), 'OverviewPage');
const Payable = lazy(() => import('./pages/bills-page').then((m) => ({ default: () => <m.BillsPage type="payable" /> })));
const Receivable = lazy(() => import('./pages/bills-page').then((m) => ({ default: () => <m.BillsPage type="receivable" /> })));
const Entries = lz(() => import('./pages/entries-page'), 'EntriesPage');
const Reconciliation = lz(() => import('./pages/reconciliation-page'), 'ReconciliationPage');
const Results = lz(() => import('./pages/results-page'), 'ResultsPage');
const Closures = lz(() => import('./pages/closures-page'), 'ClosuresPage');
const Categories = lz(() => import('./pages/catalog-pages'), 'CategoriesPage');
const Accounts = lz(() => import('./pages/catalog-pages'), 'AccountsPage');

const TABS = [
  { to: '', label: 'Visão geral', end: true },
  { to: 'pagar', label: 'Contas a pagar' },
  { to: 'receber', label: 'Contas a receber' },
  { to: 'lancamentos', label: 'Lançamentos' },
  { to: 'conciliacao', label: 'Conciliação' },
  { to: 'resultado', label: 'Resultado' },
  { to: 'fechamento', label: 'Fechamento' },
  { to: 'categorias', label: 'Categorias' },
  { to: 'contas', label: 'Contas financeiras' },
];

function FinanceLayout() {
  return (
    <>
      <PageHeader title="Financeiro" description="Contas a pagar e receber, lançamentos, fluxo de caixa e resultado da loja." className="mb-3" />
      {/* faixa de abas: rolagem só na horizontal */}
      <nav aria-label="Seções do financeiro" className="-mx-4 mb-5 overflow-x-auto overflow-y-hidden border-b px-4 sm:mx-0 sm:px-0" data-testid="finance-tabs">
        <ul className="flex min-w-max gap-1">
          {TABS.map((t) => (
            <li key={t.to}>
              <NavLink
                to={t.to === '' ? '/financeiro' : `/financeiro/${t.to}`}
                end={t.end}
                className={({ isActive }) =>
                  cn(
                    'relative flex h-11 items-center px-3.5 text-sm font-medium whitespace-nowrap transition-colors',
                    isActive ? 'text-foreground after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:bg-primary' : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {t.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <Suspense fallback={<Skeleton className="h-64 w-full" />}>
        <Outlet />
      </Suspense>
    </>
  );
}

/** Rotas do financeiro (`/financeiro/*`); a permissão `finance.read` é checada em app/routes.tsx. */
export function FinanceRoute() {
  return (
    <Routes>
      <Route element={<FinanceLayout />}>
        <Route index element={<Overview />} />
        <Route path="pagar" element={<Payable />} />
        <Route path="receber" element={<Receivable />} />
        <Route path="lancamentos" element={<Entries />} />
        <Route path="conciliacao" element={<Reconciliation />} />
        <Route path="resultado" element={<Results />} />
        <Route path="fechamento" element={<Closures />} />
        <Route path="categorias" element={<Categories />} />
        <Route path="contas" element={<Accounts />} />
        <Route path="*" element={<Navigate to="/financeiro" replace />} />
      </Route>
    </Routes>
  );
}
